import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import {
  Badge,
  Button,
  cn,
  DestructiveConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useDateFnsFormattersWithLocale,
} from "@tellescope/ui";
import { Archive, ArchiveRestore, Clipboard, CopyPlus, Ellipsis, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export interface TemplateCardProps {
  template: TellescopeModelFromName<"templates">;
  onClick?: (() => void) | { to: string; replace?: boolean };
  onDuplicate?: (() => void) | { to: string; replace?: boolean };
  onDelete?: () => void;
  onArchiveToggle?: (archived: boolean) => void;
}

export const TemplateCard = ({ template, onClick, onDuplicate, onDelete, onArchiveToggle }: TemplateCardProps) => {
  const { t } = useTranslation("templates");
  const { formatDistanceToNow } = useDateFnsFormattersWithLocale();
  const handleCopyId = () => {
    navigator.clipboard.writeText(template.id);
    toast.success(t("Template ID copied to clipboard"));
  };
  const Overlay = typeof onClick === "function" ? "button" : typeof onClick === "object" ? Link : "div";
  const hasOverlay = Overlay !== "div";
  const badgeClassName = cn("h-auto text-[10px] font-light py-0.5 rounded-full transition");

  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <>
      <div
        className="rounded-md border flex flex-col h-full overflow-clip relative group"
        data-testid={`template-card-${template.id}`}
      >
        {hasOverlay && (
          <Overlay
            to={typeof onClick === "object" ? onClick.to : ""}
            replace={typeof onClick === "object" ? onClick.replace : undefined}
            onClick={typeof onClick === "function" ? onClick : undefined}
            aria-label={template.title}
            className="absolute inset-0 z-0 transition hover:bg-gray-50 cursor-pointer"
          />
        )}
        <div
          className={cn(
            "border-b py-2 px-4 space-y-2 grow relative z-10",
            hasOverlay && "pointer-events-none",
            !!template.archivedAt && "pr-2"
          )}
        >
          <span className="text-sm flex justify-start items-center gap-2">
            <span data-testid={`template-card-title-${template.id}`}>{template.title}</span>
            {!!template.archivedAt && <Badge variant="secondary">{t("Archived")}</Badge>}
          </span>
          <div className="text-xs text-muted-foreground space-y-1">
            {!!template.type && (
              <div className="flex flex-wrap items-center gap-1">
                <span>{t("Type:")}</span>
                <Badge variant="secondary" className={badgeClassName}>
                  {template.type}
                </Badge>
              </div>
            )}
            {!!template.forChannels?.length && (
              <div className="flex flex-wrap items-center gap-1">
                <span>{t("Channels:")}</span>
                {template.forChannels.map((channel) => (
                  <Badge key={`${template.id}-channel-${channel}`} variant="secondary" className={badgeClassName}>
                    {channel}
                  </Badge>
                ))}
              </div>
            )}
            {!!template.forRoles?.length && (
              <div className="flex flex-wrap items-center gap-1">
                <span>{t("Roles:")}</span>
                {template.forRoles.map((role) => (
                  <Badge key={`${template.id}-role-${role}`} variant="secondary" className={badgeClassName}>
                    {role}
                  </Badge>
                ))}
              </div>
            )}
            {!!template.forEntityTypes?.length && (
              <div className="flex flex-wrap items-center gap-1">
                <span>{t("Entities:")}</span>
                {template.forEntityTypes.map((entity) => (
                  <Badge key={`${template.id}-entity-${entity}`} variant="secondary" className={badgeClassName}>
                    {entity}
                  </Badge>
                ))}
              </div>
            )}
            {!!template.forUserTags?.values.length && (
              <div className="flex flex-wrap items-center gap-1">
                <span>
                  {template.forUserTags.qualifier === "All Of" ? t("User tags (all):") : t("User tags (any):")}
                </span>
                {template.forUserTags.values.map((tag) => (
                  <Badge key={`${template.id}-usertag-${tag}`} variant="secondary" className={badgeClassName}>
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            <p>
              {t("Last updated:")}{" "}
              <span className="font-light">{formatDistanceToNow(template.updatedAt, { addSuffix: true })}</span>
            </p>
          </div>
        </div>
        <div
          className={cn(
            "bg-gray-100 px-4 py-1 min-h-8 flex items-center gap-1 relative z-10",
            hasOverlay && "pointer-events-none"
          )}
        >
          {template.tags?.length ? (
            template.tags.map((tag) => (
              <Badge
                key={`${template.id}-${tag}`}
                variant="outline"
                className="bg-white h-auto text-[10px] text-muted-foreground font-light py-0.5 rounded-full"
              >
                {tag}
              </Badge>
            ))
          ) : (
            <p className="italic text-xs text-muted-foreground">{t("No tags")}</p>
          )}
        </div>
        <div className="absolute top-2 right-2 z-20">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm-icon"
                aria-label={t("Template actions")}
                onClick={(e) => e.stopPropagation()}
                data-testid={`template-card-actions-${template.id}`}
                className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
              >
                <Ellipsis />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onSelect={handleCopyId} data-testid={`template-card-copy-id-${template.id}`}>
                <Clipboard />
                {t("Copy ID")}
              </DropdownMenuItem>
              {onDuplicate !== undefined &&
                (typeof onDuplicate === "function" ? (
                  <DropdownMenuItem onSelect={onDuplicate} data-testid={`template-card-duplicate-${template.id}`}>
                    <CopyPlus />
                    {t("Duplicate")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem asChild data-testid={`template-card-duplicate-${template.id}`}>
                    <Link to={onDuplicate.to} replace={onDuplicate.replace}>
                      <CopyPlus />
                      {t("Duplicate")}
                    </Link>
                  </DropdownMenuItem>
                ))}
              {onArchiveToggle &&
                (template.archivedAt ? (
                  <DropdownMenuItem
                    onSelect={() => onArchiveToggle(false)}
                    data-testid={`template-card-unarchive-${template.id}`}
                  >
                    <ArchiveRestore />
                    {t("Unarchive")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onSelect={() => onArchiveToggle(true)}
                    data-testid={`template-card-archive-${template.id}`}
                  >
                    <Archive />
                    {t("Archive")}
                  </DropdownMenuItem>
                ))}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => setIsDeleting(true)}
                    data-testid={`template-card-delete-${template.id}`}
                  >
                    <Trash2 className="text-destructive" />
                    {t("Delete")}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <DestructiveConfirmDialog
        onConfirm={() => onDelete?.()}
        open={isDeleting}
        onOpenChange={setIsDeleting}
        description={t("This will permanently delete the template. This action cannot be undone.")}
        confirmButtonTestId={`template-card-delete-confirm-${template.id}`}
      />
    </>
  );
};
